import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Upload } from "@/lib/icons";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { toast } from "sonner";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 12;
const EMPTY_PRESETS = Array.from({ length: 4 }, (_, index) => ({
  key: `portrait-${index + 1}`,
  label: `Avatar ${index + 1}`,
  image_url: "",
}));

export default function AvatarPresetManager() {
  const { settings, refresh } = useSettings();
  const [presets, setPresets] = useState(EMPTY_PRESETS);

  useEffect(() => {
    const byKey = new Map((settings?.avatar_presets || []).map((item) => [item.key, item]));
    setPresets(EMPTY_PRESETS.map((fallback) => ({ ...fallback, ...(byKey.get(fallback.key) || {}) })));
  }, [settings?.avatar_presets]);

  const update = (next) => {
    if (!Array.isArray(next)) {
      throw new Error("The server did not return the updated avatar library");
    }
    setPresets(next);
    refresh?.();
  };

  return (
    <section className="admin-panel rounded-2xl border border-white/10 p-5 sm:p-6" data-testid="avatar-preset-manager">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline text-[#c66bff]">Add Friend avatars</div>
          <h2 className="mt-1 font-display text-2xl text-white">Profile image library</h2>
          <p className="mt-1 max-w-2xl text-sm text-white/50">
            Upload four clean square portraits here. They become the selectable avatar choices on Add Friend immediately.
          </p>
        </div>
        <span className="text-xs text-white/40">JPG, PNG or WEBP · automatically optimized</span>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {presets.map((preset, index) => (
          <AvatarPresetSlot key={preset.key} preset={preset} slot={index + 1} onUpdated={update} />
        ))}
      </div>
    </section>
  );
}

function AvatarPresetSlot({ preset, slot, onUpdated }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const resolved = resolvePhotoUrl(preset.image_url);

  const upload = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes((file.type || "").toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Avatar image must be ${MAX_MB} MB or less`);
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post(`/admin/site/avatar-presets/${slot}`, data);
      if (!Array.isArray(response.data?.avatar_presets)) {
        throw new Error("The server did not return the updated avatar library");
      }
      onUpdated(response.data.avatar_presets);
      toast.success(`Avatar ${slot} updated`);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove Avatar ${slot}? Existing members using it will fall back to initials.`)) return;
    setBusy(true);
    try {
      const response = await api.delete(`/admin/site/avatar-presets/${slot}`);
      if (!Array.isArray(response.data?.avatar_presets)) {
        throw new Error("The server did not return the updated avatar library");
      }
      onUpdated(response.data.avatar_presets);
      toast.success(`Avatar ${slot} removed`);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-2.5">
      <div className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
        {resolved ? (
          <img src={resolved} alt={`Avatar ${slot}`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-white/35">
            <ImagePlus size={26} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">Empty slot</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/65 text-xs font-medium text-white">
            Working…
          </div>
        )}
      </div>
      <div className="mt-2 text-center text-xs font-semibold text-white">Avatar {slot}</div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => upload(event.target.files?.[0])}
        data-testid={`avatar-preset-${slot}-input`}
      />
      <div className="mt-2 flex gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`avatar-preset-${slot}-upload`}
          className="btn-ghost inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-lg px-2 py-2 text-[10px] font-semibold text-white disabled:opacity-50"
        >
          <Upload size={11} /> {resolved ? "Replace" : "Upload"}
        </button>
        {resolved && (
          <button
            type="button"
            disabled={busy}
            onClick={remove}
            data-testid={`avatar-preset-${slot}-delete`}
            aria-label={`Remove Avatar ${slot}`}
            className="inline-flex items-center justify-center rounded-lg border border-red-500/20 px-2.5 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
